import React from "react";
import "./confidence-indicator.scss";

export type ConfidenceLevel = "high" | "medium" | "low";

export interface ConfidenceIndicatorProps {
  /** Confidence value as a number from 0 to 100. */
  value: number;
  /** Label text displayed before the badge. Defaults to "CONFIDENCE:". */
  label?: string;
  /**
   * Explicit severity level controlling the badge color.
   * If omitted, the level is derived from `value` using `highThreshold` and `mediumThreshold`.
   */
  level?: ConfidenceLevel;
  /** Inclusive lower bound for the "high" level when auto-deriving from value. Defaults to 80. */
  highThreshold?: number;
  /** Inclusive lower bound for the "medium" level when auto-deriving from value. Defaults to 50. */
  mediumThreshold?: number;
  /** Override the badge background color. */
  backgroundColor?: string;
  /** Override the badge text color. */
  textColor?: string;
  /** Optional custom formatter for the displayed value. Defaults to `${value}%`. */
  formatValue?: (value: number) => string;
  /** Additional CSS class names applied to the root container. */
  className?: string;
}

const deriveLevel = (
  value: number,
  highThreshold: number,
  mediumThreshold: number
): ConfidenceLevel => {
  if (value >= highThreshold) return "high";
  if (value >= mediumThreshold) return "medium";
  return "low";
};

export const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  value,
  label = "Confidence:",
  level,
  highThreshold = 80,
  mediumThreshold = 50,
  backgroundColor,
  textColor,
  formatValue,
  className = "",
}) => {
  const clampedValue = Math.max(0, Math.min(100, Math.round(value)));
  const resolvedLevel =
    level ?? deriveLevel(clampedValue, highThreshold, mediumThreshold);
  const displayValue = formatValue
    ? formatValue(clampedValue)
    : `${clampedValue}%`;

  return (
    <div className={`confidence-indicator ${className}`.trim()}>
      <span className="confidence-indicator__label">{label}</span>
      <span
        className={`confidence-indicator__badge confidence-indicator__badge--${resolvedLevel}`}
        style={{ backgroundColor }}
        role="status"
        aria-label={`${label} ${displayValue}`}
      >
        <span
          className="confidence-indicator__value"
          style={{ color: textColor }}
        >
          {displayValue}
        </span>
      </span>
    </div>
  );
};

export default ConfidenceIndicator;
